/**
 * Phase 124B — Wall of Champions sidebar gateway.
 *
 * The public Wall of Champions lives at `/academy/<slug>` (zero-auth,
 * Phase 85).  Academy owners need to be able to *find* their public wall
 * from inside the dashboard — this 1-component redirect handles the
 * "internal sidebar entry → public showcase" hop without forcing the
 * user to remember their slug.
 *
 * Renders a tiny "Opening your wall…" splash while the slug resolves
 * (covers the brief moment AuthContext.user might still be hydrating).
 */
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Trophy } from 'lucide-react';

export default function WallOfChampionsGateway() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="champions-gateway-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  const slug = user?.academy_slug;
  if (slug) {
    // Hand off to the public showcase route — keeps a single source of
    // truth for the wall (no duplicate admin/public divergence).
    return <Navigate to={`/academy/${slug}`} replace />;
  }

  // No slug means the academy hasn't been backfilled yet (rare —
  // _phase85_backfill_academy_slugs runs on every academy create).
  return (
    <div className="max-w-xl mx-auto py-16 text-center" data-testid="champions-gateway-noslug">
      <Trophy className="h-10 w-10 mx-auto text-amber-500 mb-4" />
      <h2 className="text-lg font-bold mb-2">Wall of Champions</h2>
      <p className="text-sm text-muted-foreground">
        Your academy's public showcase URL is being generated. Refresh in a moment, or contact
        support if this persists.
      </p>
    </div>
  );
}
